/* eslint-disable consistent-return */
/* eslint-disable no-useless-escape */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import {
  CheckCircleOutlineRounded,
  ContentCopyRounded,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { Input } from '../../components/Input';
import { LogoCompanies } from '../../components/LogoCompanies';
import { InputPassword } from '../../components/InputPassword';
import { LoadingBackdrop } from '../../components/LoadingBackdrop';

import LogoAppleStore from '../../assets/apple.svg';
import GoogleLogo from '../../assets/googleLogo.svg';
import LogoHorizontalBlue from '../../assets/horizontal-blue-logo.svg';
import TenMeetingsLogoHorizontal from '../../assets/horizontal-white-logo.svg';

import {
  Card,
  CardContainer,
  CardTitle,
  Container,
  ContainerSteps,
  Div,
  Title,
  Text,
  TitleGov,
  Header,
  Divider,
} from './styles';

import useAuthStore from '../../store/auth';
import useCompanyStore from '../../store/companies';
import useModalToCompleteProfile from '../../store/modalToCompleteProfile';

import {
  changePasswordRequest,
  Company,
  getCompaniesByUserIdRequest,
  getCompaniesRequest,
  getTokensLocalStorage,
  getUserByEmailRequest,
  getUserLocalStorage,
  setTokensLocalStorage,
  setUserLocalStorage,
  Tokens,
  User,
} from '../../services';
import api from '../../services/api';

export function RecoverPassword() {
  const params = useParams();
  const navigate = useNavigate();

  const setUserAuthenticated = useAuthStore(
    state => state.setUserAuthenticated,
  );
  const { setMenuSelected } = useAuthStore(state => state);
  const { setSelectedCompany } = useCompanyStore(state => state);
  const { setOpenModalToCompleteProfile } = useModalToCompleteProfile(
    state => state,
  );

  const userLocalStorage = getUserLocalStorage() as User;
  const tokensLocalStorage = getTokensLocalStorage() as Tokens;

  const [listagemEmpresasMaster, setListagemEmpresasMaster] = useState([]);
  const [listagemEmpresas, setListagemEmpresas] = useState([]);
  const [stepRecover, setStepRecover] = useState('newPassword');
  const [hasHashToken, setHasHashToken] = useState<boolean>();
  const [isLoading, setIsLoading] = useState(false);

  const { email } = params;

  const formValidation = useCallback((): any => {
    return Yup.object().shape({
      email: Yup.string().email().nullable(),
      password: Yup.string()
        .min(8, 'Mínimo de 8 Dígitos')
        .required('O campo senha é obrigatório')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
          'A senha deve conter 1 maiúscula, 1 número e 1 caractere especial (como !, @, #, etc.)',
        ),
      passwordConfirmation: Yup.string()
        .min(8, 'Mínimo 8 Dígitos')
        .oneOf([Yup.ref('password'), null], 'Digite a mesma senha')
        .required('As senhas não coincidem'),
    });
  }, []);

  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(formValidation()),
  });

  const handleLoginWithCompany = useCallback(
    (company: Company) => {
      setMenuSelected('profile');
      setSelectedCompany(company);
      navigate('/home/dados-pessoais');
      setUserAuthenticated(userLocalStorage, tokensLocalStorage);
      setOpenModalToCompleteProfile(true);
    },
    [
      navigate,
      setMenuSelected,
      setOpenModalToCompleteProfile,
      setSelectedCompany,
      setUserAuthenticated,
      tokensLocalStorage,
      userLocalStorage,
    ],
  );

  const hasHashedRefreshTokenUser = useCallback(async () => {
    try {
      const response = await getUserByEmailRequest(email as string);

      if (response?.status === 200) {
        const user = response?.data;
        const hasHash = !!user?.hashedRefreshToken;

        setHasHashToken(hasHash);
      }
    } catch (err) {
      console.log(err);
    }
  }, [email]);

  useEffect(() => {
    if (email) {
      hasHashedRefreshTokenUser();
    }
  }, [email]);

  useEffect(() => {
    if (hasHashToken) {
      navigate('/');
    }
  }, [hasHashToken]);

  const handleUpdatePassword = useCallback(
    async (formData: any) => {
      try {
        setIsLoading(true);
        const { password } = formData;
        const isMobile = window.innerWidth <= 820;

        const token = params?.token as string;
        const response = await changePasswordRequest(password, token);

        if (response?.status === 201) {
          const { user, access_token, refresh_token } = response.data;

          api().defaults.headers.common.Authorization = `Bearer ${access_token}`;

          const tokens = {
            access_token,
            refresh_token,
          };

          setTokensLocalStorage(tokens);
          setUserLocalStorage(user);

          if (isMobile) {
            setStepRecover('successPasswordOnMobile');
          } else {
            setStepRecover('successPasswordOnDesktop');
          }
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    },
    [params?.token],
  );

  useEffect(() => {
    if (params?.email) {
      setValue('email', params?.email);
    }
  }, [params, setValue]);

  async function loadCompanies() {
    const response = await getCompaniesByUserIdRequest(
      userLocalStorage?.id as string,
      '',
      '0',
      '100',
    );

    setListagemEmpresas(response?.data.companies);
  }

  async function loadCompaniesMaster() {
    const response = await getCompaniesRequest('', '', '100');

    if (response?.status === 200) {
      setListagemEmpresasMaster(response?.data?.companies);
    }
  }

  useEffect(() => {
    // console.log('entrou no use');
    loadCompanies();
    loadCompaniesMaster();
  }, [stepRecover]);

  useEffect(() => {
    if (stepRecover === 'selectCompany' && listagemEmpresas?.length === 0) {
      navigate(`/home/contas`);
    }
  }, [listagemEmpresas?.length, navigate, stepRecover]);

  const contentCopy = () => {
    navigator?.clipboard
      ?.writeText(`${process.env.REACT_APP_API_URL}/`)
      .then(() => {
        toast.success('Convite copiado para a área de transferência', {
          toastId: 1,
        });
      })
      .catch(error => {
        console.error('Erro ao copiar o texto:', error);
      });
  };

  const goToGooglePlay = () => {
    window.open(
      'https://play.google.com/store/apps/details?id=com.ten.governance',
    );
  };

  const goToAppleStore = () => {
    window.open('https://apps.apple.com/us/app/ten-governance/id6447073955');
  };

  return (
    <Container>
      {isLoading && (
        <LoadingBackdrop text="Carregando" component="" loading={isLoading} />
      )}

      <Header>
        <img src={TenMeetingsLogoHorizontal} alt="logo horizontal Ten" />

        <Divider />

        <TitleGov>MÓDULO DE GOVERNANÇA</TitleGov>
      </Header>

      <ContainerSteps>
        {stepRecover === 'newPassword' && (
          <form
            onSubmit={handleSubmit(handleUpdatePassword)}
            className="form-login"
          >
            <section className="header">
              <h1 className="title">Crie sua senha</h1>
            </section>
            <Input
              className="email"
              label="E-mail"
              name="email"
              type="email"
              control={control}
              disabled
              sx={{ cursor: 'not-allowed' }}
              fullWidth
            />

            <InputPassword
              className="password"
              id="outlined-password-input"
              label="Crie uma senha"
              name="password"
              autoComplete="current-password"
              control={control}
              required
              fullWidth
            />

            <InputPassword
              className="confirm-password"
              id="outlined-password-input"
              label="Confirmar senha"
              name="passwordConfirmation"
              autoComplete="current-password"
              control={control}
              required
              fullWidth
            />

            <Button
              variant="contained"
              color="primary"
              className="submit"
              type="submit"
              fullWidth
              sx={{ height: '56px' }}
            >
              Cadastrar senha
            </Button>
          </form>
        )}
        {stepRecover === 'successPasswordOnDesktop' && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="10px"
          >
            <CheckCircleOutlineRounded
              sx={{ width: '66px', height: '66px', color: '#4CAF50;' }}
            />
            <Title>Senha criada!</Title>
            <Text>
              Sua senha foi criada com sucesso, clique no botão abaixo para
              fazer login.
            </Text>
            <Button
              variant="outlined"
              sx={{ height: '56px', padding: '6px 22px' }}
              onClick={() => navigate('/')}
            >
              Fazer Login
            </Button>
          </Box>
        )}
        {stepRecover === 'successPasswordOnMobile' && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="10px"
          >
            <CheckCircleOutlineRounded
              sx={{ width: '61px', height: '61px', color: '#4CAF50;' }}
            />
            <Title>Senha criada!</Title>
            <Text sx={{ padding: '0' }}>Para acessar, baixe o app:</Text>
            <Button
              variant="outlined"
              sx={{
                height: '49px',
                width: '202px',
                padding: '6px 22px',
                fontSize: '18px',
                fontWeight: '700',
                color: '#000000',
                borderColor: '#000000',
                textTransform: 'capitalize',
              }}
              onClick={goToAppleStore}
              startIcon={<img src={LogoAppleStore} alt="apple logo" />}
            >
              App Store
            </Button>
            <Button
              variant="outlined"
              sx={{
                height: '49px',
                width: '202px',
                padding: '6px 22px',
                fontSize: '18px',
                fontWeight: '700',
                color: '#000000',
                borderColor: '#000000',
                textTransform: 'capitalize',
              }}
              onClick={goToGooglePlay}
              startIcon={<img src={GoogleLogo} alt="google logo" />}
            >
              Google Play
            </Button>
            <Text sx={{ padding: '0', width: '202px' }}>
              Ou você pode acessar a plataforma pelo seu computador:
            </Text>
            <Button
              variant="outlined"
              sx={{
                height: '43px',
                padding: '6px 22px',
                width: '202px',
                fontWeight: '500',
                fontSize: '15px',
              }}
              onClick={contentCopy}
              endIcon={<ContentCopyRounded />}
            >
              Copiar link
            </Button>
          </Box>
        )}
        {stepRecover === 'selectCompany' && (
          <Div
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="10px"
            padding="10px 60px"
            width="100%"
          >
            <Text className="title-company" sx={{ marginBottom: '15px' }}>
              Selecione a empresa para continuar
            </Text>
            {userLocalStorage?.profile === 'UN1' ? (
              <CardContainer>
                {listagemEmpresasMaster?.map((c: Company) => (
                  <Card key={c?.id} onClick={() => handleLoginWithCompany(c)}>
                    <Box width="25%" height="95px" marginLeft="20px">
                      <LogoCompanies
                        url={
                          c.logo_url
                            ? (c?.logo_url as string)
                            : LogoHorizontalBlue
                        }
                        height="70%"
                        boxWidth="5vw"
                        maxWidth="100%"
                        width="250px"
                        justifyContent="center"
                      />
                    </Box>
                    <Tooltip title={c.name}>
                      <CardTitle>{c.name}</CardTitle>
                    </Tooltip>
                  </Card>
                ))}
              </CardContainer>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                gap="20px"
                width="100%"
              >
                {listagemEmpresas ? (
                  listagemEmpresas?.map((c: Company) => (
                    <Card key={c?.id} onClick={() => handleLoginWithCompany(c)}>
                      <Box width="25%" height="95px" marginLeft="20px">
                        <LogoCompanies
                          url={
                            c.logo_url
                              ? (c?.logo_url as string)
                              : LogoHorizontalBlue
                          }
                          height="70%"
                          maxWidth="100%"
                          boxWidth="5vw"
                          width="250px"
                          justifyContent="center"
                        />
                      </Box>
                      <Tooltip title={c.name}>
                        <CardTitle>{c.name}</CardTitle>
                      </Tooltip>
                    </Card>
                  ))
                ) : (
                  <Box width="100%" display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            )}
          </Div>
        )}
      </ContainerSteps>
    </Container>
  );
}
